import type {PropsWithChildren} from 'react';
import React, {useEffect, useMemo} from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import {ThemeProvider} from '@mui/material/styles';
import {useLocalizationContextProvider} from 'localization';
import {LocalizationContext} from 'localization/context/localizationContext';
import {createCustomTheme} from 'theme';
import {
  selectSettingsFontSizePct,
  selectSettingsPreferredLocale,
} from 'services/settings';
import {useAppSelector} from 'store/hooks';
import {ThemedSnackbarProvider} from './ThemedSnackbarProvider';

export const ThemedApp: React.FC<PropsWithChildren> = ({children}) => {
  const preferredLocale = useAppSelector(selectSettingsPreferredLocale);
  const theme = useMemo(
    () => createCustomTheme(preferredLocale),
    [preferredLocale],
  );
  const localizationContext = useLocalizationContextProvider(preferredLocale);
  const fontSizePct = useAppSelector(selectSettingsFontSizePct);

  useEffect(() => {
    document.documentElement.style.setProperty('font-size', `${fontSizePct}%`);
  }, [fontSizePct]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline>
        <LocalizationContext.Provider value={localizationContext}>
          <ThemedSnackbarProvider>{children}</ThemedSnackbarProvider>
        </LocalizationContext.Provider>
      </CssBaseline>
    </ThemeProvider>
  );
};
